/**
 * Excel number formats as a chart shows them — a port of
 * App\Services\Docx\Studio\Presentation\Charts\ChartNumberFormat, so tick
 * and data labels of an edited chart read exactly as the server writes
 * them: Estonian region (decimal comma, a non-breaking space between
 * thousands, "." as the localised date separator) with English month names,
 * what Word for Mac on the parity host draws. Shared vectors:
 * tests/Fixtures/charts/number-format-vectors.json.
 *
 * Plus the other direction for the data sheet: Estonian number input
 * ("1 234,5", "-12", "1234.5" pasted from an English sheet) and dates.
 */

const GROUP = '\u00A0'
const DECIMAL = ','
const DATE_SEPARATOR = '.'
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

/** Built-in formats Office localises: the literal separator becomes the locale's date separator. */
const LOCALISED_DATES = ['mmm\\-yy', 'mmm-yy', 'd\\-mmm\\-yy', 'd-mmm-yy', 'd\\-mmm', 'd-mmm', 'm/d/yyyy', 'm/d/yy']

const DAY_MS = 86400000
/** 9999-12-31, the last date Excel holds (ChartTable's limit on a date category). */
const MAX_DATE_SERIAL = 2958465
const EPOCH_1900 = Date.UTC(1899, 11, 30)
const EPOCH_1904 = Date.UTC(1904, 0, 1)

/**
 * PHP's round() half away from zero, step for step as PHP 8.4 computes it
 * (_php_math_round, PHP_ROUND_HALF_UP), which number_format() rounds with:
 * 0.285 → 0.29, while a value just below a tie its shortest decimal form
 * hides (-28980.299994999998 at 5 places) stays below it.
 */
function roundHalfUp(value, decimals) {
  if (!Number.isFinite(value) || value === 0) return value
  if (decimals === 0 && value === Math.trunc(value)) return value
  const exponent = decimals <= 22 ? Number(`1e${decimals}`) : 10 ** decimals
  let integral = value >= 0 ? Math.floor(value * exponent) : Math.ceil(value * exponent)
  const next = value >= 0 ? integral + 1 : integral - 1
  if (next / exponent === value) integral = next
  if (Math.abs(integral) >= 1e16) return value
  const sign = integral < 0 || Object.is(integral, -0) ? -1 : 1
  if (Math.abs(value) >= Math.abs((integral + sign * 0.5) / exponent)) integral += sign
  return integral / exponent
}

/** PHP's number_format(). */
function numberFormat(value, decimals, decimalPoint, thousands) {
  const rounded = roundHalfUp(value, decimals)
  const negative = rounded < 0
  const [integer, fraction] = Math.abs(rounded).toFixed(decimals).split('.')
  const grouped = thousands ? integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) : integer
  return (negative ? '-' : '') + grouped + (decimals > 0 ? decimalPoint + fraction : '')
}

/** Excel's General: integers as they are, up to ten significant digits otherwise. */
export function general(value) {
  if (!Number.isFinite(value)) return ''
  if (value === Math.floor(value) && Math.abs(value) < 1e11) return numberFormat(value, 0, '', '')
  const abs = Math.abs(value)
  if (abs >= 1e11 || abs < 1e-9) {
    return value.toExponential(5).replace('e', 'E').replace(/E([+-])(\d)$/, 'E$1$2').replace('.', DECIMAL)
  }
  const digits = Math.max(0, 10 - Math.floor(Math.log10(abs)) - 1)
  // As the server: zeros trimmed even without a decimal point (PHP's rtrim).
  const text = numberFormat(value, Math.min(digits, 9), '.', '').replace(/0+$/, '').replace(/\.+$/, '')
  return text.replace('.', DECIMAL)
}

function sections(code) {
  const out = []
  let current = ''
  let quoted = false
  for (let i = 0; i < code.length; i++) {
    const char = code[i]
    if (char === '"') quoted = !quoted
    else if (char === '\\' && !quoted && i + 1 < code.length) {
      current += char + code[++i]
      continue
    } else if (char === ';' && !quoted) {
      out.push(current)
      current = ''
      continue
    }
    current += char
  }
  out.push(current)
  return out
}

function isDate(section) {
  const bare = section.replace(/"[^"]*"|\\./g, '')
  return /[dmyhs]/i.test(bare) && !/[0#?]/.test(bare)
}

function dateText(serial, section, localised, date1904) {
  let days = Math.floor(serial)
  if (!date1904 && days < 61) days++ // Excel's phantom 1900-02-29.
  const date = new Date((date1904 ? EPOCH_1904 : EPOCH_1900) + days * DAY_MS)
  const year = date.getUTCFullYear()
  const month = date.getUTCMonth()
  const day = date.getUTCDate()
  const two = (n) => String(n).padStart(2, '0')
  let out = ''
  for (let i = 0; i < section.length; ) {
    const char = section[i]
    if (char === '"') {
      let end = section.indexOf('"', i + 1)
      if (end === -1) end = section.length
      out += section.slice(i + 1, end)
      i = end + 1
      continue
    }
    if (char === '\\' && i + 1 < section.length) {
      const literal = section[i + 1]
      out += localised && (literal === '-' || literal === '/') ? DATE_SEPARATOR : literal
      i += 2
      continue
    }
    const lower = char.toLowerCase()
    if (lower === 'd' || lower === 'm' || lower === 'y') {
      let run = 0
      while (i + run < section.length && section[i + run].toLowerCase() === lower) run++
      if (lower === 'y') out += run >= 3 ? String(year).padStart(4, '0') : two(year % 100)
      else if (lower === 'd') out += run === 1 ? String(day) : run === 2 ? two(day) : run === 3 ? DAYS[date.getUTCDay()].slice(0, 3) : DAYS[date.getUTCDay()]
      else out += run === 1 ? String(month + 1) : run === 2 ? two(month + 1) : run === 3 ? MONTHS[month].slice(0, 3) : run === 4 ? MONTHS[month] : MONTHS[month].slice(0, 1)
      i += run
      continue
    }
    out += localised && (char === '-' || char === '/') ? DATE_SEPARATOR : char
    i++
  }
  return out
}

function literal(part) {
  return part.replace(/"([^"]*)"|\\(.)|_.|\*./gs, (match, quoted, escaped) => {
    if (quoted) return quoted
    if (escaped) return escaped
    if (match.startsWith('_')) return ' '
    return ''
  })
}

function numberText(value, section) {
  const percent = (section.replace(/"[^"]*"|\\./g, '').match(/%/g) ?? []).length
  value *= 100 ** percent
  const parts = /^([\s\S]*?)([#0?][#0?,]*(?:\.[#0?]*)?)([\s\S]*)$/.exec(section)
  if (!parts) return literal(section)
  const [, prefix, digits, suffix] = parts
  const dot = digits.indexOf('.')
  const decimals = dot >= 0 ? digits.slice(dot + 1).replace(/[^0?#]/g, '').length : 0
  const integerPart = dot >= 0 ? digits.slice(0, dot) : digits
  // Trailing commas scale by thousands; a comma between placeholders groups.
  const scale = integerPart.length - integerPart.replace(/,+$/, '').length
  value /= 1000 ** scale
  const grouped = integerPart.replace(/,+$/, '').includes(',')

  let text = numberFormat(Math.abs(value), decimals, DECIMAL, grouped ? GROUP : '')
  if (dot >= 0) {
    const optional = digits.slice(dot + 1).replace(/[^#?]/g, '').length
    if (optional > 0) {
      const trimmed = text.replace(/0+$/, '')
      const minimum = text.length - optional
      text = trimmed.length < minimum ? text.slice(0, minimum) : trimmed
      text = text.replace(/,+$/, '')
    }
  }
  const minimumIntegers = (integerPart.match(/0/g) ?? []).length
  if (minimumIntegers === 0 && text.startsWith(`0${DECIMAL}`)) text = text.slice(1)
  else if (minimumIntegers === 0 && text === '0') text = ''

  const negative = value < 0 && roundHalfUp(Math.abs(value), decimals) > 0
  return (negative ? '-' : '') + literal(prefix) + text + literal(suffix)
}

/** A value in a chart format code, as the server's ChartNumberFormat::format(). */
export function formatChartNumber(value, formatCode, date1904 = false) {
  const code = String(formatCode ?? '').trim()
  if (code === '' || code.toLowerCase() === 'general') return general(value)
  const all = sections(code)
  let section = all[0]
  let negativeOwnSection = false
  if (value < 0 && all[1] !== undefined) {
    section = all[1]
    negativeOwnSection = true
  } else if (value === 0 && all[2] !== undefined) {
    section = all[2]
  }
  // Colours and locale/condition blocks never print.
  section = section.replace(/\[[^\]]*\]/g, '')
  if (section.trim().toLowerCase() === 'general') return general(value)
  if (isDate(section)) return dateText(value, section, LOCALISED_DATES.includes(code), date1904)
  return numberText(negativeOwnSection ? Math.abs(value) : value, section)
}

// --------------------------------------------------------------- input

const SPACES = /[\s\u00A0\u202F\u2009]/g

/**
 * A number the admin typed or pasted: Estonian first ("1 234,5", comma
 * decimal, spaces between thousands), and what English sheets paste
 * ("1,234.5", "1234.5"). Empty → { ok: true, value: null } (clears the
 * cell); anything else → { ok: false }.
 */
export function parseNumberInput(input) {
  let text = String(input ?? '').replace(SPACES, '').replace(/^\u2212/, '-')
  if (text === '') return { ok: true, value: null }
  const comma = text.lastIndexOf(',')
  const dot = text.lastIndexOf('.')
  if (comma >= 0 && dot >= 0) {
    // Both: the later one is the decimal separator.
    text = comma > dot ? text.replace(/\./g, '').replace(',', '.') : text.replace(/,/g, '')
  } else if (comma >= 0) {
    if (text.indexOf(',') !== comma) return { ok: false }
    text = text.replace(',', '.')
  } else if (dot >= 0 && text.indexOf('.') !== dot) {
    // "1.234.567": dots between thousands.
    text = text.replace(/\./g, '')
  }
  if (!/^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i.test(text)) return { ok: false }
  const value = Number(text)
  return Number.isFinite(value) ? { ok: true, value } : { ok: false }
}

const MONTH_NAMES = [
  ['jan', 'jaan'],
  ['feb', 'veebr', 'veeb'],
  ['mar', 'mär', 'märts'],
  ['apr', 'apr'],
  ['may', 'mai'],
  ['jun', 'juuni', 'jun'],
  ['jul', 'juuli', 'jul'],
  ['aug'],
  ['sep', 'sept'],
  ['oct', 'okt'],
  ['nov'],
  ['dec', 'dets'],
]

/** Days since 1899-12-30 (Excel's serial) of a UTC date. */
export function serialOf(year, month, day = 1) {
  return Math.round((Date.UTC(year, month, day) - EPOCH_1900) / DAY_MS)
}

function fullYear(text) {
  const year = Number(text)
  return text.length <= 2 ? 2000 + year : year
}

/**
 * A date category the admin typed: "03.2024", "1.3.2024", "2024-03",
 * "Mar.24" / "märts 2024" (English or Estonian month), or an Excel serial.
 * → { ok: true, value: serial | null } or { ok: false }.
 */
export function parseDateInput(input) {
  const text = String(input ?? '').trim().toLowerCase()
  if (text === '') return { ok: true, value: null }
  let match = /^(\d{1,2})[./](\d{1,2})[./](\d{2}|\d{4})$/.exec(text)
  if (match) return validDate(fullYear(match[3]), Number(match[2]) - 1, Number(match[1]))
  match = /^(\d{1,2})[./-](\d{4})$/.exec(text)
  if (match) return validDate(Number(match[2]), Number(match[1]) - 1, 1)
  match = /^(\d{4})-(\d{1,2})(?:-(\d{1,2}))?$/.exec(text)
  if (match) return validDate(Number(match[1]), Number(match[2]) - 1, match[3] ? Number(match[3]) : 1)
  match = /^([a-zäöüõ]+)\.?[\s.\-/]*(\d{2}|\d{4})$/.exec(text)
  if (match) {
    const month = MONTH_NAMES.findIndex((names) => names.some((name) => match[1].startsWith(name) || name.startsWith(match[1]) && match[1].length >= 3))
    if (month >= 0) return validDate(fullYear(match[2]), month, 1)
  }
  // A serial past 9999-12-31 is no date Excel holds: the server refuses it (ChartTable), formatChartNumber cannot draw it.
  if (/^\d+(\.\d+)?$/.test(text) && Number(text) <= MAX_DATE_SERIAL) return { ok: true, value: Number(text) }
  return { ok: false }
}

function validDate(year, month, day) {
  if (month < 0 || month > 11 || day < 1 || day > 31 || year < 1900 || year > 9999) return { ok: false }
  const date = new Date(Date.UTC(year, month, day))
  if (date.getUTCMonth() !== month) return { ok: false }
  return { ok: true, value: serialOf(year, month, day) }
}

/** What a number cell shows in the sheet: its series' format code, "General" as Estonian input reads it. */
export function displayNumber(value, formatCode) {
  if (value === null || value === undefined) return ''
  return formatChartNumber(value, formatCode)
}

/** The plain editable text of a number: Estonian, no grouping, every significant digit. */
export function editableNumber(value) {
  if (value === null || value === undefined) return ''
  return String(value).replace('.', DECIMAL)
}
